import { Brand10, Brand90, Danger500, Neutral300, Success600, Warning500 } from '@src/utils/colors';

interface FoundationGaugeProps {
  present: number;
  total: number;
}

const RADIUS = 78;
const ARC_LENGTH = Math.PI * RADIUS;

/**
 * Semicircular foundation-coverage gauge, ported from the reference tool's
 * gaugeSvg(): track + value arc colored by coverage, tick marks at category
 * boundaries, a needle, and the big present/total readout.
 */
export const FoundationGauge = ({ present, total }: FoundationGaugeProps) => {
  const frac = total ? present / total : 0;
  let color = Danger500;
  if (frac >= 1) {
    color = Success600;
  } else if (frac >= 0.5) {
    color = Warning500;
  }

  const ticks = Array.from({ length: Math.max(total - 1, 0) }, (_, i) => {
    const angle = Math.PI * (1 - (i + 1) / total);
    return {
      key: i,
      x1: 100 + (RADIUS - 10) * Math.cos(angle),
      y1: 102 - (RADIUS - 10) * Math.sin(angle),
      x2: 100 + (RADIUS + 10) * Math.cos(angle),
      y2: 102 - (RADIUS + 10) * Math.sin(angle)
    };
  });
  const needleAngle = Math.PI * (1 - frac);
  const needleX = 100 + (RADIUS - 16) * Math.cos(needleAngle);
  const needleY = 102 - (RADIUS - 16) * Math.sin(needleAngle);

  return (
    <svg
      className='digital-audit-gauge'
      viewBox='0 0 200 118'
      width='180'
      height='106'
      role='img'
      aria-label={`Foundation coverage: ${present} of ${total} categories present`}
    >
      <path
        d={`M22,102 A${RADIUS},${RADIUS} 0 0 1 178,102`}
        fill='none'
        stroke={Neutral300}
        strokeWidth='15'
        strokeLinecap='round'
      />
      <path
        d={`M22,102 A${RADIUS},${RADIUS} 0 0 1 178,102`}
        fill='none'
        stroke={color}
        strokeWidth='15'
        strokeLinecap='round'
        strokeDasharray={`${(frac * ARC_LENGTH).toFixed(1)} ${ARC_LENGTH.toFixed(1)}`}
      />
      {ticks.map(tick => (
        <line
          key={tick.key}
          x1={tick.x1.toFixed(1)}
          y1={tick.y1.toFixed(1)}
          x2={tick.x2.toFixed(1)}
          y2={tick.y2.toFixed(1)}
          stroke={Brand10}
          strokeWidth='3'
        />
      ))}
      <line
        x1='100'
        y1='102'
        x2={needleX.toFixed(1)}
        y2={needleY.toFixed(1)}
        stroke={Brand90}
        strokeWidth='3.5'
        strokeLinecap='round'
        strokeOpacity='0.25'
      />
      <circle cx='100' cy='102' r='6' fill={Brand90} />
      <text x='100' y='88' textAnchor='middle' className='digital-audit-gauge-label'>
        {present}/{total}
      </text>
    </svg>
  );
};
